// src/components/ErrorBoundary/ErrorBoundary.tsx

import React, { ErrorInfo, ReactNode } from 'react';
import ErrorRecoveryComponent from './ErrorRecoveryComponent';
import { logError } from '../../utils/errorUtils';

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: (error: Error | null, resetError: () => void) => ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Log error to tracking service
        // Spread errorInfo and convert componentStack null → undefined to satisfy logError's type signature
        logError(error, { ...errorInfo, componentStack: errorInfo.componentStack ?? undefined });
    }

    resetError = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    render() {
        const { hasError, error, errorInfo } = this.state;
        const { children, fallback } = this.props;

        if (hasError) {
            return fallback ? (
                fallback(error, this.resetError)
            ) : (
                <ErrorRecoveryComponent
                    error={error}
                    errorInfo={errorInfo}
                    onReset={this.resetError}
                />
            );
        }

        return children;
    }
}

export default ErrorBoundary;
